/**
 * Presentation metadata for `internalLinks` entries in blog frontmatter (#562).
 *
 * `BlogPost.jsx` previously used a binary `course` / else ternary, so every type
 * that was not `course` rendered the assessment badge and the assessment blurb.
 * A survey of the corpus found 421 entries across 15 distinct spellings, so the
 * fall-through was far wider than the `pathway` case that prompted this: `blog`
 * (114), `tool` (29), `series` (12), `ebook` (10), `assessment-tool` (6),
 * `article` (3), `page` (3), `service` (2) and `pathway` (1) were all labelled
 * "Assessment".
 *
 * Some frontmatter quotes the value (`type: 'course'`, 36 uses), so normalise
 * before lookup: strip surrounding quotes, trim, lowercase.
 *
 * Keep `ASSESSMENT` as the fallback so an unknown or missing type degrades to
 * the historical behaviour rather than rendering an empty card.
 */
const ASSESSMENT = {
  label: '🔍 Assessment',
  blurb: 'Comprehensive assessment to identify gaps and maturity level.',
};

const ARTICLE = {
  label: '📝 Article',
  blurb: 'Further reading on this topic.',
};

export const INTERNAL_LINK_TYPES = {
  course: {
    label: '📚 Training',
    blurb: 'Hands-on training to build practical skills and achieve certification.',
  },
  assessment: ASSESSMENT,
  'assessment-tool': {
    label: '🔍 Assessment tool',
    blurb: 'A free self-assessment you can run against your own environment.',
  },
  pathway: {
    label: '🧭 Pathway',
    blurb: 'A sequenced set of courses that builds one capability end to end.',
  },
  tool: {
    label: '🛠️ Tool',
    blurb: 'A free interactive tool you can use straight away.',
  },
  ebook: {
    label: '📘 Ebook',
    blurb: 'A downloadable guide covering this subject in depth.',
  },
  series: {
    label: '🗂️ Series',
    blurb: 'A multi-part series covering this subject in sequence.',
  },
  service: {
    label: '🤝 Service',
    blurb: 'Consulting support to apply this in your organisation.',
  },
  blog: ARTICLE,
  article: ARTICLE,
  page: {
    label: '🔗 Related',
    blurb: 'More detail on this topic.',
  },
};

/** Normalise a frontmatter type: strip surrounding quotes, trim, lowercase. */
export const normaliseLinkType = (type) =>
  String(type ?? '')
    .trim()
    .replace(/^['"]|['"]$/g, '')
    .toLowerCase();

/** Badge label and blurb for a link type, falling back to the assessment card. */
export const getInternalLinkMeta = (type) =>
  INTERNAL_LINK_TYPES[normaliseLinkType(type)] || ASSESSMENT;

export default getInternalLinkMeta;
